import type { AttributeField } from "./types.ts";

/**
 * Renders a survey from `prod_data` ALONE — no template, no `data` dependency.
 *
 * prod_data is self-describing: each entry carries its own structure
 * (`field_type`, `options`, `section_path`, `order`) plus its answer
 * (`final_survey_answer`). So we rebuild the `AttributeField[]` tree directly
 * from prod_data: group entries by `section_path` into section nodes, order by
 * `order`, and render each question by its `field_type` (+ `options`). The
 * resulting shape is identical to the legacy `data` tree, so every existing
 * renderer/consumer works unchanged.
 *
 * Which source a property uses is an explicit per-property flag
 * (`property_attributes.survey_source`): `'prod_data'` → render standalone here;
 * anything else → the legacy `data` tree.
 */

type SectionRef = { id: string; title: string };

type ProdEntry = {
  final_survey_answer?: unknown;
  /** ENG-498 answer-status contract: 'not_applicable'/'unknown' carry a blank
   *  final_survey_answer — the status IS the answer, never typed as text. */
  answer_status?: string | null;
  leaf_id?: string | null;
  question_title?: string | null;
  field_type?: string | null;
  options?: string[] | null;
  section_path?: SectionRef[] | null;
  order?: number | null;
  human_confidence?: number | null;
  evidence?: unknown;
};

/** prod_data evidence is `[{url,...}]` or string[]; the UI leaf wants string[]. */
function normalizeEvidence(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => (typeof x === "string" ? x : (x as { url?: string } | null)?.url))
    .filter((u): u is string => typeof u === "string" && u.length > 0);
}

/**
 * Build one leaf AttributeField from a prod_data entry.
 *
 * Under the answer-status contract (ENG-498), a reviewer's "Can't answer"
 * decision leaves `final_survey_answer` blank and records the status
 * separately (`answer_status: 'not_applicable' | 'unknown'`, the latter with
 * a closed reason — illegible, no_access, requires_specialist…) — surveyors
 * no longer type "N/A" into the value. Every export N/A rule (format-column
 * cell survival, GC Evaluation skip, asset gap-fill — all in
 * gcEvaluationFill.ts's `isNotApplicable`) still keys off that literal text,
 * so both statuses are re-synthesized as the "N/A" sentinel here to keep them
 * working unchanged. Not_applicable and unknown are deliberately treated the
 * same at export: either way nobody has a usable answer to show.
 */
function makeLeaf(key: string, e: ProdEntry): AttributeField {
  const id = e.leaf_id ?? key;
  const type = e.field_type ?? "input";
  // prod_data stores a flat answer; coerce it to the shape the field type
  // expects (renderers assume multiSelect/photos are arrays; area is a string).
  let value: unknown =
    e.answer_status === "not_applicable" || e.answer_status === "unknown"
      ? "N/A"
      : (e.final_survey_answer ?? null);
  if (type === "multiSelect" || type === "photos") {
    value = Array.isArray(value) ? value : value == null || value === "" ? [] : [value];
  } else if (type === "area" && value != null && typeof value !== "string") {
    value = String(value);
  }
  const evidence = normalizeEvidence(e.evidence);
  return {
    id,
    ref_id: id,
    type,
    title: e.question_title ?? "",
    value,
    ...(Array.isArray(e.options) && e.options.length ? { options: e.options } : {}),
    ...(evidence.length ? { evidence } : {}),
    ...(e.human_confidence != null ? { confidence: e.human_confidence } : {}),
  } as unknown as AttributeField;
}

/**
 * A real answer, or an explicit not_applicable/unknown decision — anything
 * that isn't just a still-untouched placeholder. Used to prefer a decided
 * duplicate entry over a stale unreviewed one (see buildSurveyFromProdData).
 */
function isDecidedEntry(e: ProdEntry): boolean {
  if (e.answer_status === "not_applicable" || e.answer_status === "unknown") return true;
  const v = e.final_survey_answer;
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * Build the survey tree from prod_data alone. Only entries carrying `field_type`
 * (the self-describing marker) are rendered — legacy/orphan QA entries that
 * don't correspond to a real survey leaf are skipped. Sections come from
 * `section_path`; sibling order from `order`.
 *
 * A question can be present TWICE under different keys with the same
 * `leaf_id`: the original capture (keyed by the raw leaf id) and a review
 * decision written under a canonicalized key (e.g. the catalog's dev code —
 * see prodDataToSurveyRows' titleToDev/refToDev bridge in
 * server/src/db/pps.ts, commit a13e81d4). The stale capture-time entry is
 * frequently left behind, still unanswered, alongside the reviewed one — on
 * one real survey this hit 70 of 159 questions. Rendering both would either
 * duplicate the question or, worse, let whichever entry sorts later silently
 * overwrite the other's answer once buildAnswerMap keys by this shared
 * leaf_id — so only one entry per leaf_id survives, preferring whichever is
 * actually decided (a real answer, or an explicit not_applicable/unknown)
 * over a still-untouched duplicate.
 */
export function buildSurveyFromProdData(
  prodData: Record<string, unknown> | null | undefined,
): AttributeField[] {
  const root: AttributeField[] = [];
  // pathKey ("" = root, "/a/b" = nested) → the node whose `fields` we append to.
  const fieldsByPath = new Map<string, AttributeField[]>();
  fieldsByPath.set("", root);

  const rawEntries = Object.entries(prodData ?? {})
    .map(([key, raw]) => ({ key, e: (raw && typeof raw === "object" ? raw : {}) as ProdEntry }))
    .filter(({ e }) => typeof e.field_type === "string" && e.field_type.length > 0);

  const byLeafId = new Map<string, { key: string; e: ProdEntry }>();
  for (const item of rawEntries) {
    const dedupeKey = item.e.leaf_id ?? item.key;
    const existing = byLeafId.get(dedupeKey);
    if (!existing || (!isDecidedEntry(existing.e) && isDecidedEntry(item.e))) {
      byLeafId.set(dedupeKey, item);
    }
  }
  const entries = [...byLeafId.values()].sort((a, b) => (a.e.order ?? 0) - (b.e.order ?? 0));

  for (const { key, e } of entries) {
    const path = Array.isArray(e.section_path) ? e.section_path : [];
    let parentKey = "";
    for (const seg of path) {
      const childKey = `${parentKey}/${seg.id}`;
      if (!fieldsByPath.has(childKey)) {
        const node = {
          id: seg.id,
          ref_id: seg.id,
          title: seg.title,
          type: "node",
          fields: [] as AttributeField[],
        } as unknown as AttributeField;
        fieldsByPath.get(parentKey)!.push(node);
        fieldsByPath.set(childKey, (node as unknown as { fields: AttributeField[] }).fields);
      }
      parentKey = childKey;
    }
    fieldsByPath.get(parentKey)!.push(makeLeaf(key, e));
  }

  return root;
}

/**
 * The per-property render gate. `survey_source === 'prod_data'` → render the
 * self-describing survey from prod_data; otherwise the legacy `data` tree.
 * Falls back to `data` if prod_data has no self-describing entries (safety).
 */
export function resolveSurveyAttributes(args: {
  surveySource: string | null | undefined;
  prodData: Record<string, unknown> | null | undefined;
  data: AttributeField[];
}): { fields: AttributeField[]; source: "prod_data" | "data" } {
  const { surveySource, prodData, data } = args;
  if (surveySource === "prod_data" && prodData && Object.keys(prodData).length > 0) {
    const fields = buildSurveyFromProdData(prodData);
    if (fields.length > 0) return { fields, source: "prod_data" };
  }
  return { fields: data ?? [], source: "data" };
}
